import React, { useMemo } from 'react';
import { Alert, Box, Skeleton, Typography } from '@mui/material';

import { InfoTooltip } from '../../../../components/core';
import {
  AgenticPersonality,
  AgentWidgetMetricsResponse,
} from '../../../../store/modules/merchant/types';
import { formatCount, formatRate, percentShares } from './formatMetrics';

type AgentFunnelPanelProps = {
  personalities: AgenticPersonality[];
  data?: AgentWidgetMetricsResponse;
  isLoading: boolean;
  errorMessage: string | null;
};

type BreakdownSegment = {
  key: string;
  label: string;
  count: number;
  color: string;
};

type MetricBreakdownProps = {
  title: string;
  segments: BreakdownSegment[] | undefined;
  emptyMessage: string;
  unavailableMessage?: string;
};

const SEGMENT_COLORS = ['#1E1E1E', '#8AD6D2', '#757575', '#C4C4C4'];
const STAGES = [
  { key: 'saw', label: 'Saw the launcher' },
  { key: 'opened', label: 'Opened an agent' },
  { key: 'used', label: 'Talked to the agent' },
] as const;
const INPUT_SEGMENTS = [
  { key: 'typed', label: 'Typed', color: '#1E1E1E' },
  { key: 'spoken', label: 'Spoken', color: '#8AD6D2' },
  { key: 'unknown', label: 'Unattributed', color: '#C4C4C4' },
] as const;
const MIC_SEGMENTS = [
  { key: 'granted', label: 'Allowed', color: '#8AD6D2' },
  { key: 'denied', label: 'Denied', color: '#757575' },
  { key: 'failed', label: 'Failed', color: '#BC3003' },
] as const;

export const AgentFunnelPanel = ({
  personalities,
  data,
  isLoading,
  errorMessage,
}: AgentFunnelPanelProps) => {
  const clicks = useMemo(
    () => agentClickSegments(data?.clicks ?? [], personalities),
    [data?.clicks, personalities],
  );
  const input = data?.input;

  return (
    <Box
      component="section"
      aria-label="Launcher activity"
      data-testid="ella-agent-funnel"
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'secondary.main',
        borderRadius: '8px',
        backgroundColor: 'background.paper',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="medium" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
          Launcher activity
        </Typography>
        <InfoTooltip
          title="Counts are summed by day and event group. The same browser may count more than once across the date range. Stages are counted independently. Stage percentages use launcher views as the baseline."
          placement="top"
          ariaLabel="More information about launcher activity"
        />
      </Box>
      <Typography variant="small" color="text.secondary" component="p" sx={{ mt: 0.5, mb: 1.5 }}>
        Daily browser counts across all agents.
      </Typography>

      {errorMessage ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : isLoading ? (
        <Box role="status" aria-label="Loading launcher activity">
          {STAGES.map((stage) => (
            <Skeleton key={stage.key} variant="rectangular" height={56} sx={{ mb: 1.5 }} />
          ))}
        </Box>
      ) : !data?.hasEvents ? (
        <Typography variant="small" color="text.secondary" data-testid="ella-funnel-empty">
          No launcher activity in this date range.
        </Typography>
      ) : (
        <>
          <LauncherStages data={data} />
          <MetricBreakdown
            title="How shoppers started"
            segments={
              input &&
              INPUT_SEGMENTS.filter(
                (segment) => segment.key !== 'unknown' || input.unknown > 0,
              ).map((segment) => ({ ...segment, count: input[segment.key] }))
            }
            emptyMessage="No input recorded in this date range."
            unavailableMessage="Input breakdown is not available."
          />
          <MetricBreakdown
            title="Microphone results"
            segments={MIC_SEGMENTS.map((segment) => ({ ...segment, count: data.mic[segment.key] }))}
            emptyMessage="No microphone results in this date range."
          />
          {personalities.length > 1 && (
            <MetricBreakdown
              title="Which agent they chose"
              segments={clicks}
              emptyMessage="No agent-attributed clicks in this date range."
            />
          )}
        </>
      )}
    </Box>
  );
};

const LauncherStages = ({ data }: { data: AgentWidgetMetricsResponse }) => (
  <Box component="ol" aria-label="Launcher activity" sx={{ listStyle: 'none', m: 0, p: 0 }}>
    {STAGES.map((stage, index) => {
      const share = data.saw > 0 ? data[stage.key] / data.saw : null;
      return (
        <Box
          component="li"
          key={stage.key}
          data-testid={`ella-metric-${stage.key}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: '32px minmax(0, 1fr) 76px',
            gap: 1.5,
            alignItems: 'center',
            py: 1.5,
            borderBottom: index === STAGES.length - 1 ? 'none' : '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(107, 114, 128, 0.14)',
              color: '#374151',
              typography: 'small',
              fontWeight: 700,
            }}
          >
            {index + 1}
          </Box>
          <Box>
            <Typography variant="small">{stage.label}</Typography>
            <Typography
              variant="small"
              color="text.secondary"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatCount(data[stage.key])}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Box
              aria-hidden
              sx={{
                width: '100%',
                height: 6,
                borderRadius: 99,
                overflow: 'hidden',
                backgroundColor: '#E8EAED',
              }}
            >
              <Box
                data-testid={`ella-launcher-fill-${stage.key}`}
                sx={{
                  height: '100%',
                  width: `${Math.min(share ?? 0, 1) * 100}%`,
                  borderRadius: 99,
                  backgroundColor: '#6B7280',
                }}
              />
            </Box>
            <Typography variant="small" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatRate(share)}
            </Typography>
          </Box>
        </Box>
      );
    })}
  </Box>
);

const MetricBreakdown = ({
  title,
  segments,
  emptyMessage,
  unavailableMessage = 'Breakdown is not available.',
}: MetricBreakdownProps) => {
  const total = segments?.reduce((sum, segment) => sum + segment.count, 0) ?? 0;
  const percentages = percentShares(segments?.map((segment) => segment.count) ?? []);

  return (
    <Box
      component="section"
      aria-label={title}
      sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
    >
      <Typography variant="small">{title}</Typography>
      {!segments ? (
        <Typography variant="small" color="text.secondary" component="p" sx={{ mt: 0.75 }}>
          {unavailableMessage}
        </Typography>
      ) : total === 0 ? (
        <Typography variant="small" color="text.secondary" component="p" sx={{ mt: 0.75 }}>
          {emptyMessage}
        </Typography>
      ) : (
        <>
          <Box
            aria-hidden
            sx={{
              display: 'flex',
              height: 8,
              borderRadius: 99,
              overflow: 'hidden',
              my: 0.75,
              backgroundColor: '#E8EAED',
            }}
          >
            {segments.map((segment) => (
              <Box
                key={segment.key}
                sx={{ width: `${(segment.count / total) * 100}%`, backgroundColor: segment.color }}
              />
            ))}
          </Box>
          <Box
            component="ul"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, listStyle: 'none', m: 0, p: 0 }}
          >
            {segments.map((segment, index) => (
              <Box component="li" aria-label={segment.label} key={segment.key}>
                <Typography variant="small" component="p">
                  <Box component="span" aria-hidden sx={{ color: segment.color }}>
                    ●
                  </Box>{' '}
                  {segment.label}
                </Typography>
                <Typography
                  variant="small"
                  component="p"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {percentages[index]}% · {formatCount(segment.count)}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

function agentClickSegments(
  clicks: AgentWidgetMetricsResponse['clicks'],
  personalities: AgenticPersonality[],
): BreakdownSegment[] {
  const names = new Map(
    personalities.map((personality) => [personality.personalityId, personality.displayName]),
  );
  return [...clicks]
    .sort((a, b) => b.clicks - a.clicks || a.personalityId.localeCompare(b.personalityId))
    .map((row, index) => ({
      key: row.personalityId,
      label: names.get(row.personalityId) || row.personalityId,
      count: row.clicks,
      color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
    }));
}
